// client/src/components/map/assetVisualRules.ts
//
// How an asset looks on every LexiStar surface, as DATA (ST-00276 foundation, with ST-00253 /
// ST-00244, 2026-10-19). Pure TypeScript — no React, no DOM — so the same palette and rules can
// later be produced by the server per organisation and consumed here unchanged:
//
//   AssetState  — a small typed description of an asset at a moment: its operational context,
//                 trip stage, motion, data age (offline / stale), maintenance kind, a declared
//                 delay, flags (driver reminded, alert open, …), and whether LexiStar knows it.
//   VisualRule  — { id, when: Condition, layer: 'base' | 'overlay', visual }. Conditions are
//                 plain data (field equality / membership / flags, combined with all / any /
//                 not), never functions, so a rule set serialises to JSON.
//   resolveAssetVisual(state, rules, palette) — the FIRST matching base rule gives the key
//                 (colour) and label; every matching overlay rule, by priority, adds a shape
//                 (the dashed "no recent position" marker), a badge or a ring.
//   legendFromRules(rules, palette) — the legend, generated from the active rules.
//
// DEFAULT_RULES are today's behaviour: the base colour is the operational status (the control
// room's precedence: breakdown, planned maintenance, maintenance, a declared delay, offline, a
// delay zone or a stop en route, the load cycle — a weighbridge by the trip's side — a dispatched
// trip not moving yet, the depot, moving without a trip, else parked); an asset LexiStar does not
// know falls back to its motion. Two overlays exist today: the dashed marker for a stale / offline
// position, and the "driver reminded" badge on a dispatched trip's chip.

export type AssetStatusKey =
  | 'to_source'
  | 'at_source'
  | 'loaded'
  | 'at_destination'
  | 'empty'
  | 'delay'
  | 'parked'
  | 'maintenance'
  | 'planned_maintenance'
  | 'breakdown'
  | 'offline'
  | 'unallocated';

/** Display order: the load cycle, then the trucks that need a look or stand idle. */
export const ASSET_STATUS_ORDER: AssetStatusKey[] = [
  'to_source',
  'at_source',
  'loaded',
  'at_destination',
  'empty',
  'delay',
  'parked',
  'maintenance',
  'planned_maintenance',
  'breakdown',
  'offline',
  'unallocated',
];

/** App-surface colours for one theme. */
export interface AssetStatusSurface {
  solid: string;
  on: string;
  text: string;
  tint: string;
  border: string;
}

export interface AssetStatusTokens {
  key: AssetStatusKey;
  label: string;
  /** One line: what an asset with this key is doing. */
  hint: string;
  /** A text glyph (LS-09 §4: never colour alone). */
  glyph: string;
  /** Map / marker colour (both themes). */
  marker: string;
  /** Drawn hollow (dashed) on the marker: no recent position. */
  hollow?: boolean;
  light: AssetStatusSurface;
  dark: AssetStatusSurface;
}

const INK = '#0f172a';
const WHITE = '#ffffff';

const SKY = {
  marker: '#38bdf8',
  light: { solid: '#38bdf8', on: INK, text: '#075985', tint: '#f0f9ff', border: '#bae6fd' },
  dark: { solid: '#38bdf8', on: INK, text: '#7dd3fc', tint: '#0c2434', border: '#1f5a80' },
};
const GREEN = {
  marker: '#16a34a',
  light: { solid: '#16a34a', on: WHITE, text: '#166534', tint: '#f0fdf4', border: '#bbf7d0' },
  dark: { solid: '#16a34a', on: WHITE, text: '#86efac', tint: '#122a17', border: '#22593a' },
};
const PURPLE = {
  marker: '#9333ea',
  light: { solid: '#9333ea', on: WHITE, text: '#6b21a8', tint: '#faf5ff', border: '#e9d5ff' },
  dark: { solid: '#a855f7', on: INK, text: '#d8b4fe', tint: '#221434', border: '#4f2f7a' },
};
const SLATE = {
  marker: '#94a3b8',
  light: { solid: '#94a3b8', on: INK, text: '#334155', tint: '#f1f5f9', border: '#cbd5e1' },
  dark: { solid: '#94a3b8', on: INK, text: '#c9d1d9', tint: '#161d26', border: '#374250' },
};

export const ASSET_STATUS: Record<AssetStatusKey, AssetStatusTokens> = {
  to_source: {
    key: 'to_source',
    label: 'To source',
    hint: 'Empty, on the way to load',
    glyph: '▷',
    ...SKY,
  },
  at_source: { key: 'at_source', label: 'At source', hint: 'Loading', glyph: '⬒', ...GREEN },
  loaded: {
    key: 'loaded',
    label: 'Travelling full',
    hint: 'Loaded, on the way to offload',
    glyph: '▶',
    marker: '#1e40af',
    light: { solid: '#1e40af', on: WHITE, text: '#1e40af', tint: '#eff6ff', border: '#bfdbfe' },
    dark: { solid: '#3b82f6', on: WHITE, text: '#93c5fd', tint: '#10203a', border: '#28508a' },
  },
  at_destination: {
    key: 'at_destination',
    label: 'At destination',
    hint: 'Offloading',
    glyph: '⬓',
    ...GREEN,
  },
  empty: {
    key: 'empty',
    label: 'Travelling empty',
    hint: 'Offloaded, on the way back',
    glyph: '▷',
    ...SKY,
  },
  // The control room's Delayed group and the maintenance module's Delay category. The solid is
  // orange-600 (#ea580c): orange-500, the maintenance timeline's bar, holds only 2.8:1 on white.
  delay: {
    key: 'delay',
    label: 'Delayed',
    hint: 'Held up: a delay zone, stopped en route, a queue, no driver',
    glyph: '⏳',
    marker: '#ea580c',
    light: { solid: '#ea580c', on: WHITE, text: '#9a3412', tint: '#fff7ed', border: '#fed7aa' },
    dark: { solid: '#ea580c', on: WHITE, text: '#fdba74', tint: '#2b1608', border: '#6b3a16' },
  },
  parked: {
    key: 'parked',
    label: 'Parked',
    hint: 'Standing, no trip under way',
    glyph: 'P',
    ...SLATE,
  },
  maintenance: {
    key: 'maintenance',
    label: 'In maintenance',
    hint: 'Under maintenance or out of service',
    glyph: '⚒',
    ...PURPLE,
  },
  // The maintenance module's categories (ST-00249): Planned maintenance purple, Breakdown rose,
  // Delay orange — the same keys as the control room's sub-groups and its Delayed group.
  planned_maintenance: {
    key: 'planned_maintenance',
    label: 'Planned maintenance',
    hint: 'Booked into the workshop: a service, tyres, an inspection',
    glyph: '⚒',
    ...PURPLE,
  },
  breakdown: {
    key: 'breakdown',
    label: 'Breakdown',
    hint: 'Broken down: not available until it is repaired',
    glyph: '✖',
    marker: '#e11d48',
    light: { solid: '#e11d48', on: WHITE, text: '#9f1239', tint: '#fff1f2', border: '#fecdd3' },
    dark: { solid: '#e11d48', on: WHITE, text: '#fda4af', tint: '#2e1119', border: '#71303f' },
  },
  offline: {
    key: 'offline',
    label: 'Offline / no data',
    hint: 'No recent position',
    glyph: '⌀',
    marker: '#64748b',
    hollow: true,
    light: { solid: '#64748b', on: WHITE, text: '#475569', tint: '#ffffff', border: '#94a3b8' },
    dark: { solid: '#8b98a5', on: INK, text: '#adb8c2', tint: '#161b22', border: '#6c7a89' },
  },
  unallocated: {
    key: 'unallocated',
    label: 'Unallocated',
    hint: 'Moving without a trip',
    glyph: '?',
    ...SLATE,
  },
};

export const isAssetStatusKey = (v: unknown): v is AssetStatusKey =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(ASSET_STATUS, v);

/** A palette: key → tokens (the default is ASSET_STATUS; a server could send its own). */
export type AssetPalette = Record<AssetStatusKey, AssetStatusTokens>;

// ── The asset state ──────────────────────────────────────────────────────────────────────────

/** Motion, normalised: the engine's MOVING / IDLING / STOPPED / PARKED / TOWED, or Live View's. */
export type AssetMotion = 'moving' | 'idling' | 'stopped' | 'parked';

/** A declared maintenance block's kind (the maintenance module / the manual override). */
export type AssetMaintenanceKind = 'breakdown' | 'planned' | 'under_maintenance' | 'out_of_service';

/** Flags an overlay can show. */
export type AssetFlag =
  'driver_reminded' | 'awaiting_first_report' | 'alert_open' | 'deviating' | 'no_driver';

/** What the rules read about an asset at a moment (plain data in). */
export interface AssetState {
  /** The operational context (LS-04 §3.2; a manual override's mode wins), else null. */
  context?: string | null;
  /** The active trip's stage, else null. */
  stage?: string | null;
  motion?: AssetMotion | null;
  /** No data for the offline threshold (the engine's 90 minutes), or no data at all. */
  offline?: boolean;
  /** The position is old enough for this surface to draw it as "no recent position". */
  stale?: boolean;
  maintenance?: AssetMaintenanceKind | null;
  /** An open delay event (the maintenance module, ST-00249). */
  delayed?: boolean;
  flags?: readonly AssetFlag[];
  /** false: LexiStar has no status for this asset (a tracker outside the register): motion only. */
  known?: boolean;
}

/** The fields a condition can test. */
export type AssetField = Exclude<keyof AssetState, 'flags'>;
type Scalar = string | number | boolean | null;

/** A condition over an AssetState: plain data, serialisable. */
export type Condition =
  | { always: true }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition }
  | { field: AssetField; is: Scalar }
  | { field: AssetField; in: Scalar[] }
  | { flag: AssetFlag };

/** What a rule contributes. */
export interface RuleVisual {
  /** Base rules: the colour key. */
  key?: AssetStatusKey;
  /** The words (default: the key's label). */
  label?: string;
  /** Overlay: draw the marker dashed / hollow. */
  shape?: 'dashed';
  /** Overlay: a badge (its words; the surface draws its own tag). */
  badge?: string;
  /** Overlay: a ring in a key's colour. */
  ring?: AssetStatusKey;
}

export interface VisualRule {
  id: string;
  when: Condition;
  layer: 'base' | 'overlay';
  /** Overlays: higher first (ties keep rule order). */
  priority?: number;
  visual: RuleVisual;
  /** The legend's words for this rule (default: the label / the key's hint). */
  legend?: string;
}

/** One overlay on an asset. */
export interface AssetOverlay {
  ruleId: string;
  shape?: 'dashed';
  badge?: string;
  ring?: AssetStatusKey;
}

/** What the rules resolve an asset to (visual out). */
export interface AssetVisual {
  key: AssetStatusKey;
  label: string;
  /** The base rule that matched. */
  ruleId: string;
  /** Draw the marker hollow and dashed (an overlay, or a hollow key such as offline). */
  hollow: boolean;
  overlays: AssetOverlay[];
}

/** `MOVING` / `moving` → AssetMotion (TOWED counts as moving). */
export function normaliseMotion(m: string | null | undefined): AssetMotion | null {
  switch (String(m ?? '').toUpperCase()) {
    case 'MOVING':
    case 'TOWED':
      return 'moving';
    case 'IDLING':
      return 'idling';
    case 'STOPPED':
      return 'stopped';
    case 'PARKED':
      return 'parked';
    default:
      return null;
  }
}

// ── Matching ─────────────────────────────────────────────────────────────────────────────────

const DEFAULT_FALSE: AssetField[] = ['offline', 'stale', 'delayed'];

const valueOf = (state: AssetState, field: AssetField): Scalar => {
  const v = state[field];
  if (v === undefined) {
    if (field === 'known') return true;
    return DEFAULT_FALSE.includes(field) ? false : null;
  }
  return v as Scalar;
};

/** Whether a condition holds for a state. */
export function matches(cond: Condition, state: AssetState): boolean {
  if ('always' in cond) return true;
  if ('all' in cond) return cond.all.every((c) => matches(c, state));
  if ('any' in cond) return cond.any.some((c) => matches(c, state));
  if ('not' in cond) return !matches(cond.not, state);
  if ('flag' in cond) return (state.flags ?? []).includes(cond.flag);
  if ('in' in cond) return cond.in.includes(valueOf(state, cond.field));
  return valueOf(state, cond.field) === cond.is;
}

// ── The default rules (today's behaviour) ────────────────────────────────────────────────────

const ctx = (...contexts: string[]): Condition =>
  contexts.length === 1
    ? { field: 'context', is: contexts[0] as string }
    : { field: 'context', in: contexts };
const TRANSIT_CONTEXTS = ['EN_ROUTE_TO_SOURCE', 'IN_TRANSIT_LOADED', 'IN_TRANSIT_EMPTY'];
/** Before the load: a truck at a weighbridge with one of these stages is on the source side. */
const BEFORE_LOADED_STAGES = ['DISPATCHED', 'ACKNOWLEDGED', 'EN_ROUTE_TO_SOURCE', 'AT_SOURCE'];
const UNKNOWN: Condition = { field: 'known', is: false };

export const DEFAULT_RULES: VisualRule[] = [
  // An asset LexiStar does not know: its motion only.
  {
    id: 'untracked-offline',
    layer: 'base',
    when: { all: [UNKNOWN, { field: 'offline', is: true }] },
    visual: { key: 'offline', label: 'Offline' },
    legend: 'Offline, not in LexiStar',
  },
  {
    id: 'untracked-moving',
    layer: 'base',
    when: { all: [UNKNOWN, { field: 'motion', is: 'moving' }] },
    visual: { key: 'unallocated', label: 'Moving' },
    legend: 'Moving, not in LexiStar',
  },
  {
    id: 'untracked',
    layer: 'base',
    when: UNKNOWN,
    visual: { key: 'parked', label: 'Parked' },
    legend: 'Standing, not in LexiStar',
  },
  // Declared states: they hold while the tracker is silent.
  {
    id: 'breakdown',
    layer: 'base',
    when: { field: 'maintenance', is: 'breakdown' },
    visual: { key: 'breakdown' },
  },
  {
    id: 'planned-maintenance',
    layer: 'base',
    when: { field: 'maintenance', is: 'planned' },
    visual: { key: 'planned_maintenance' },
  },
  {
    id: 'maintenance',
    layer: 'base',
    when: {
      any: [
        { field: 'maintenance', in: ['under_maintenance', 'out_of_service'] },
        ctx('UNDER_MAINTENANCE', 'OUT_OF_SERVICE'),
      ],
    },
    visual: { key: 'maintenance' },
  },
  {
    id: 'declared-delay',
    layer: 'base',
    when: { field: 'delayed', is: true },
    visual: { key: 'delay' },
  },
  {
    id: 'offline',
    layer: 'base',
    when: { field: 'offline', is: true },
    visual: { key: 'offline' },
  },
  // Held up on the way.
  { id: 'delay-zone', layer: 'base', when: ctx('IN_DELAY_ZONE'), visual: { key: 'delay' } },
  {
    id: 'trip-on-hold',
    layer: 'base',
    when: { field: 'stage', is: 'EXCEPTION_HOLD' },
    visual: { key: 'delay' },
  },
  {
    id: 'stopped-en-route',
    layer: 'base',
    when: { all: [ctx(...TRANSIT_CONTEXTS), { field: 'motion', in: ['stopped', 'parked'] }] },
    visual: { key: 'delay' },
  },
  // The load cycle (a weighbridge counts on the trip's side).
  {
    id: 'weighbridge-source',
    layer: 'base',
    when: {
      all: [
        ctx('AT_WEIGHBRIDGE'),
        {
          any: [
            { field: 'stage', is: null },
            { field: 'stage', in: BEFORE_LOADED_STAGES },
          ],
        },
      ],
    },
    visual: { key: 'at_source' },
  },
  {
    id: 'weighbridge-destination',
    layer: 'base',
    when: ctx('AT_WEIGHBRIDGE'),
    visual: { key: 'at_destination' },
  },
  { id: 'to-source', layer: 'base', when: ctx('EN_ROUTE_TO_SOURCE'), visual: { key: 'to_source' } },
  { id: 'at-source', layer: 'base', when: ctx('AT_SOURCE'), visual: { key: 'at_source' } },
  { id: 'loaded', layer: 'base', when: ctx('IN_TRANSIT_LOADED'), visual: { key: 'loaded' } },
  {
    id: 'at-destination',
    layer: 'base',
    when: ctx('AT_DESTINATION'),
    visual: { key: 'at_destination' },
  },
  { id: 'empty', layer: 'base', when: ctx('IN_TRANSIT_EMPTY'), visual: { key: 'empty' } },
  {
    id: 'dispatched',
    layer: 'base',
    when: { field: 'stage', in: ['DISPATCHED', 'ACKNOWLEDGED'] },
    visual: { key: 'to_source' },
  },
  // Idle.
  { id: 'depot', layer: 'base', when: ctx('AT_DEPOT', 'STANDBY'), visual: { key: 'parked' } },
  {
    id: 'moving-no-trip',
    layer: 'base',
    when: { field: 'motion', is: 'moving' },
    visual: { key: 'unallocated' },
  },
  { id: 'parked', layer: 'base', when: { always: true }, visual: { key: 'parked' } },
  // Overlays that exist today.
  {
    id: 'no-recent-position',
    layer: 'overlay',
    priority: 10,
    when: {
      any: [
        { field: 'stale', is: true },
        { field: 'offline', is: true },
      ],
    },
    visual: { shape: 'dashed' },
    legend: 'Dashed: no recent position',
  },
  {
    id: 'driver-reminded',
    layer: 'overlay',
    priority: 5,
    when: { all: [{ flag: 'driver_reminded' }, { field: 'stage', is: 'DISPATCHED' }] },
    visual: { badge: 'Driver reminded' },
    legend: 'Driver reminded to accept the trip',
  },
];

// ── Resolving ────────────────────────────────────────────────────────────────────────────────

export interface ResolveOptions {
  rules?: readonly VisualRule[];
  palette?: AssetPalette;
}

/** The visual for an asset state: the first matching base rule, plus the matching overlays. */
export function resolveAssetVisual(
  state: AssetState,
  { rules = DEFAULT_RULES, palette = ASSET_STATUS }: ResolveOptions = {}
): AssetVisual {
  const base = rules.find((r) => r.layer === 'base' && r.visual.key && matches(r.when, state));
  const key: AssetStatusKey = base?.visual.key ?? 'parked';
  const overlays: AssetOverlay[] = rules
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.layer === 'overlay' && matches(r.when, state))
    .sort((a, b) => (b.r.priority ?? 0) - (a.r.priority ?? 0) || a.i - b.i)
    .map(({ r }) => ({
      ruleId: r.id,
      ...(r.visual.shape ? { shape: r.visual.shape } : {}),
      ...(r.visual.badge ? { badge: r.visual.badge } : {}),
      ...(r.visual.ring ? { ring: r.visual.ring } : {}),
    }));
  return {
    key,
    label: base?.visual.label ?? palette[key].label,
    ruleId: base?.id ?? 'none',
    hollow: !!palette[key].hollow || overlays.some((o) => o.shape === 'dashed'),
    overlays,
  };
}

/** The key alone (the colour). */
export const resolveAssetKey = (state: AssetState, options?: ResolveOptions): AssetStatusKey =>
  resolveAssetVisual(state, options).key;

/** Whether a visual carries an overlay (by rule id). */
export const hasOverlay = (visual: AssetVisual, ruleId: string): boolean =>
  visual.overlays.some((o) => o.ruleId === ruleId);

// ── States from what the pages hold ──────────────────────────────────────────────────────────

/** A trip stage → the context the engine derives from it (engine trip.ts tripContextOf). */
export const STAGE_CONTEXT: Record<string, string> = {
  EN_ROUTE_TO_SOURCE: 'EN_ROUTE_TO_SOURCE',
  AT_SOURCE: 'AT_SOURCE',
  IN_TRANSIT_LOADED: 'IN_TRANSIT_LOADED',
  AT_DESTINATION: 'AT_DESTINATION',
  IN_TRANSIT_EMPTY: 'IN_TRANSIT_EMPTY',
};

/**
 * A trip stage at a moment (Replay): the context the engine derives from it. Motion is only
 * "moving" or unknown — a replay's short stops are not the engine's "stopped en route".
 */
export const assetStateOfStage = (
  stage: string | null | undefined,
  moving = false
): AssetState => ({
  stage: stage ?? null,
  context: (stage && STAGE_CONTEXT[stage]) || null,
  motion: moving ? 'moving' : null,
});

/** A two-axis status (LS-04 §3) as the pages hold it, without the row around it. */
export interface TwoAxisStatusLike {
  context?: string | null;
  motion?: string | null;
  overlays?: readonly string[] | null;
  override?: { mode?: string | null } | null;
}

const OVERLAY_FLAGS: Record<string, AssetFlag> = {
  ALERT_OPEN: 'alert_open',
  DEVIATING: 'deviating',
  NO_DRIVER: 'no_driver',
};

export const assetStateOfStatus = (status: TwoAxisStatusLike | null | undefined): AssetState => {
  const overlays = Array.isArray(status?.overlays) ? (status!.overlays as readonly string[]) : [];
  return {
    context: status?.override?.mode || status?.context || null,
    motion: normaliseMotion(status?.motion),
    offline: overlays.includes('OFFLINE'),
    flags: overlays.map((o) => OVERLAY_FLAGS[o]).filter((f): f is AssetFlag => !!f),
  };
};

/** Motion as Live View derives it from speed + report age (no LexiStar status). */
export type MotionStatus = 'moving' | 'idling' | 'parked' | 'offline';

/** An asset LexiStar does not know, by its motion (Live View's speed + report age, Trips). */
export const assetStateOfMotion = (
  motion: MotionStatus | string | null | undefined
): AssetState => ({
  known: false,
  motion:
    motion === 'moving'
      ? 'moving'
      : motion === 'idling'
        ? 'idling'
        : motion === 'parked'
          ? 'parked'
          : null,
  offline: motion === 'offline',
  stale: motion === 'offline',
});

// ── The legend ───────────────────────────────────────────────────────────────────────────────

export interface LegendEntry {
  /** 'key': a colour; 'overlay': a shape or a badge. */
  kind: 'key' | 'overlay';
  id: string;
  label: string;
  hint?: string;
  key?: AssetStatusKey;
  shape?: 'dashed';
  badge?: string;
}

/**
 * The legend of a rule set: one entry per colour key a base rule can give (palette order;
 * optionally only `keys`), then one per overlay rule (optionally only `overlayIds`).
 */
export function legendFromRules(
  { rules = DEFAULT_RULES, palette = ASSET_STATUS }: ResolveOptions = {},
  { keys, overlayIds }: { keys?: readonly AssetStatusKey[]; overlayIds?: readonly string[] } = {}
): LegendEntry[] {
  const used = new Set(
    rules
      .filter((r) => r.layer === 'base' && r.visual.key)
      .map((r) => r.visual.key as AssetStatusKey)
  );
  const colours: LegendEntry[] = ASSET_STATUS_ORDER.filter(
    (k) => used.has(k) && (!keys || keys.includes(k))
  ).map((k) => ({ kind: 'key', id: k, key: k, label: palette[k].label, hint: palette[k].hint }));
  const marks: LegendEntry[] = rules
    .filter((r) => r.layer === 'overlay' && (!overlayIds || overlayIds.includes(r.id)))
    .map((r) => ({
      kind: 'overlay',
      id: r.id,
      label: r.legend ?? r.visual.badge ?? r.id,
      ...(r.visual.shape ? { shape: r.visual.shape } : {}),
      ...(r.visual.badge ? { badge: r.visual.badge } : {}),
    }));
  return [...colours, ...marks];
}
